import moment from "moment";

import { formatDatetime, parseDate } from "react-lib/utils/dateUtils";

import { stageOf } from "../common/queueDomainV4";

// ---------- รูปแบบ Check out ----------

/** รูปแบบการ Check out: ค่า checkout_cause ของ backend, ชื่อ, คำอธิบาย */
export const CHECKOUT_TYPES = [
  { value: "BY_APPROVAL", label: "By approval", desc: "แพทย์อนุญาตให้กลับบ้าน" },
  { value: "ADMIT", label: "Admit", desc: "รับไว้เป็นผู้ป่วยใน" },
  { value: "ER", label: "ER", desc: "ส่งต่อห้องฉุกเฉิน" },
  { value: "REFER_OUT", label: "Refer Out", desc: "ส่งตัวไปรักษาที่สถานพยาบาลอื่น" },
  {
    value: "REJECT",
    label: "Reject",
    desc: "ผู้รับบริการปฏิเสธการรักษา / ไม่สมัครใจรับบริการต่อ",
  },
  { value: "DEAD", label: "Dead", desc: "เสียชีวิต" },
  { value: "WAIT_RESULT", label: "Wait Result", desc: "รอผลตรวจ (นัดกลับมาฟังผล)" },
] as const;

export type CheckoutCause = (typeof CHECKOUT_TYPES)[number]["value"];

/** encounter ส่ง checkout_cause เป็นชื่อ (เช่น "Refer Out"), EMR ส่งเป็นค่า enum */
export const checkoutTypeOf = (value: unknown) =>
  CHECKOUT_TYPES.find((type) => type.value === value || type.label === value) ?? null;

// ---------- วันเวลาเสียชีวิต ----------

/** death_datetime ของ BE: "DD/MM/พ.ศ.-HH:mm" ("" = ยังกรอกไม่ครบ) */
export const deathDatetimeOf = (date: string, time: string): string =>
  parseDate(date, true)?.isValid() && moment(time, "HH:mm", true).isValid()
    ? `${date}-${time}`
    : "";

// ---------- สถานะ Check out ----------

export type CheckoutEncounter = {
  id?: number | null;
  status?: string | null;
  checkout_cause?: string | number | null;
  encounter_checkout_time?: string | null;
  encounter_checkout_user_fullname?: string | null;
  discharge_note?: string | null;
};

export type CheckoutInfo = {
  /** แบบ · คำอธิบาย */
  typeText: string;
  /** "04/11/2569 14:31 · โดย ชื่อ" */
  atText: string;
  note: string;
};

/** ขั้นของคิวที่กด Check out ได้ */
const CHECKOUT_STAGES = ["in-doctor", "wait-lab", "wait-pharmacy"];

/** สถานะก่อนออกจากห้องตรวจ: checkout_cause ที่ค้างอยู่คือ Check out ที่ยกเลิกไปแล้ว */
const BEFORE_CHECKOUT_STATUSES = [
  "WAIT_TRIAGE",
  "ARRIVING",
  "ARRIVED",
  "SCREENED",
  "IN_QUEUE",
  "IN_EXAM",
  "WAIT_RESULT",
  "RECEIVE_RESULT",
];

/** ฟิลด์ Check out จาก GET core/encounter/<id>/ (แถวคิวไม่มีฟิลด์เหล่านี้) */
export const checkoutEncounterOf = (detail: Record<string, unknown>): CheckoutEncounter => ({
  id: typeof detail.id === "number" ? detail.id : null,
  status: typeof detail.status === "string" ? detail.status : null,
  checkout_cause: (detail.checkout_cause as CheckoutEncounter["checkout_cause"]) ?? null,
  encounter_checkout_time: (detail.encounter_checkout_time as string | null) ?? null,
  encounter_checkout_user_fullname:
    (detail.encounter_checkout_user_fullname as string | null) ?? null,
  discharge_note: (detail.discharge_note as string | null) ?? null,
});

/** encounter ที่เปิดอยู่ + ฟิลด์ Check out จาก detail ของ encounter เดียวกัน */
export const checkoutEncounterFor = (
  encounter?: CheckoutEncounter | null,
  detail?: CheckoutEncounter | null,
): CheckoutEncounter | null => {
  if (!encounter) return null;
  if (!detail || detail.id !== encounter.id) return encounter;
  return { ...detail, status: encounter.status ?? detail.status };
};

/** ยกเลิก Check out ได้เฉพาะก่อนชำระเงิน (BE ไม่มี action ย้อนจาก รอจ่ายยา / เสร็จสิ้น) */
export const canCancelCheckout = (encounter?: CheckoutEncounter | null): boolean =>
  ["CHECKED_OUT", "WAIT_PAY"].includes(encounter?.status ?? "");

export const isCheckedOut = (encounter?: CheckoutEncounter | null): boolean =>
  !!encounter?.checkout_cause && !BEFORE_CHECKOUT_STATUSES.includes(encounter.status ?? "");

export const canCheckout = (encounter?: CheckoutEncounter | null): boolean =>
  !!encounter &&
  !isCheckedOut(encounter) &&
  CHECKOUT_STAGES.includes(stageOf(encounter)?.key ?? "");

export const checkoutInfoOf = (encounter?: CheckoutEncounter | null): CheckoutInfo | null => {
  if (!encounter || !isCheckedOut(encounter)) return null;
  const type = checkoutTypeOf(encounter.checkout_cause);
  const at = formatDatetime(encounter.encounter_checkout_time);
  const by = encounter.encounter_checkout_user_fullname || "-";

  return {
    typeText: type ? `${type.label} · ${type.desc}` : String(encounter.checkout_cause),
    atText: at ? `${at} · โดย ${by}` : `โดย ${by}`,
    note: (encounter.discharge_note ?? "").trim(),
  };
};
